import React, { Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Switch } from "react-router-dom";

import {
    formatIcon
} from "./utilities/FormatUtilities";

import FourOhFourLayout from "./components/fourohfour/FourOhFourLayout";
import { appSetRoutes } from "./redux/app/appActions";
import AppLayout from "./components/application/AppLayout";

export default function Routes() {
    const dispatch = useDispatch();

    // App State
    const user = useSelector(state => state.user);

    // Component State
    const [routeJSX, setRouteJSX] = useState(null);

    useEffect(() => {
        // console.log('Routes: UseEffect, [userContext.user.authorized]');

        if (user.authorized && !routeJSX) {

            const routes = [];
            const roleFeatures = user.roleFeatures;
			let adminRoute = false;

            // TODO: Read/Enable Features
            // TODO: Look up icon
            // TODO: Expand to submenus when needed
            // TODO: Add Collapsible for Parent nodes

            // **************************************************
            //      All Users Features
            // **************************************************
            const allUsersFeatures = roleFeatures.filter(feature => feature.featureType === 0);

            if (allUsersFeatures) {

				for (let i = 0; i < allUsersFeatures.length; i++) {
					if (allUsersFeatures[i].enabled) {
						if (allUsersFeatures[i].name.toLowerCase() === 'home') {

							// **************************************************
							//      Package: Home
							// **************************************************
							const lazyHomeLayout = React.lazy(() => import("./packages/home/HomeLayout"));

							routes.push({
								seqNo: allUsersFeatures[i].seqNo,
								label: 'Home-Nav',
								pageTitle: 'Home-PageTitle',
								tooltip: '',
								route: '/',
								navDrawer: true,
								navParent: '',
								icon: formatIcon('HomeIcon'),
								component: lazyHomeLayout
							});

						} else if (allUsersFeatures[i].name.toLowerCase() === 'project hub') {

							// **************************************************
							//      Package: Project Hub
							// **************************************************

							const lazyProjectHubLayout = React.lazy(() => import("./packages/projecthub/ProjectHubLayout"));

							routes.push({
								seqNo: allUsersFeatures[i].seqNo,
								label: 'Project Hub-Nav',
								pageTitle: 'Project Hub-PageTitle',
								tooltip: '',
								route: '/projecthub',
								navDrawer: true,
								navParent: '',
								icon: formatIcon('HubIcon'),
								component: lazyProjectHubLayout
							});

						} else if (allUsersFeatures[i].name.toLowerCase() === 'emissions') {

							// **************************************************
							//      Package: Emissions Inventory
							// **************************************************

							if (allUsersFeatures[i].enabled) {

								const lazyEmissionsInventoryLayout = React.lazy(() => import("./packages/emissions/EmissionsInventoryLayout"));

								routes.push({
									seqNo: allUsersFeatures[i].seqNo,
									label: 'Emissions Inventory-Nav',
									pageTitle: 'Emissions Inventory-PageTitle',
									tooltip: '',
									route: '/emissions/inventory/:tab?',
									navDrawer: false,
									navParent: '',
									icon: formatIcon('ListIcon'),
									component: lazyEmissionsInventoryLayout
								});
							}

						} else if (allUsersFeatures[i].name.toLowerCase() === 'swmm') {

							// **************************************************
							//      Package: SWMM RTK
							// **************************************************
							if (allUsersFeatures[i].enabled) {

								const lazySWMMLayout = React.lazy(() => import("./packages/swmm/SWMMLayout"));

								routes.push({
									seqNo: allUsersFeatures[i].seqNo,
									label: 'SWMM-Nav',
									pageTitle: 'SWMM-PageTitle',
									tooltip: '',
									route: '/swmm/run/:tab?',
									navDrawer: false,
									navParent: '',
									icon: formatIcon('EPASWMMIcon'),
									component: lazySWMMLayout
								});
							}
						}
					}
				}

            }

            // **************************************************
            // **************************************************
            //      Admin Kinda Features
            // **************************************************
            // **************************************************

			// **************************************************
			//      Package: Emissions Inventory - Setup
			// **************************************************
			const emissionsInventoryFeature = roleFeatures.filter(feature => feature.featureType === 225);

			if (emissionsInventoryFeature && emissionsInventoryFeature.length > 0 && emissionsInventoryFeature[0].enabled) {

				const lazyEmissionsInventoryLayout = React.lazy(() => import("./packages/emissions/EmissionsInventorySetupLayout"));

				routes.push({
					seqNo: emissionsInventoryFeature[0].seqNo,
					label: 'Emissions Inventory Setup-Nav',
					pageTitle: 'Emissions Inventory Setup-PageTitle',
					tooltip: '',
					route: '/emissions/setup/:tab?',
					navDrawer: true,
					navParent: 'Admin-Nav',
					icon: formatIcon('ListIcon'),
					component: lazyEmissionsInventoryLayout
				});

				adminRoute = true;
			}

			// **************************************************
			//      Package: SWMM RTK - Setup
			// **************************************************
			const swmmFeature = roleFeatures.filter(feature => feature.featureType === 235);

			if (swmmFeature && swmmFeature.length > 0 && swmmFeature[0].enabled) {

				const lazySWMMLayout = React.lazy(() => import("./packages/swmm/SWMMSetupLayout"));

				routes.push({
					seqNo: swmmFeature[0].seqNo,
					label: 'SWMM Setup-Nav',
					pageTitle: 'SWMM Setup-PageTitle',
					tooltip: '',
					route: '/swmm/setup/:tab?',
					navDrawer: true,
					navParent: 'Admin-Nav',
					icon: formatIcon('EPASWMMIcon'),
					component: lazySWMMLayout
				});

				adminRoute = true;
			}

            // **************************************************
            //      Package: Data Loading
            // **************************************************

            // NOTE: Loaders are seamingly more 'core' than a feature...
            // Ideally features are added and handle all routes etc. so
            // this will definitely change with the next package. Furthermore,
            // loaders are no longer standard i.e. can be configured
            // and added bringing new challenges to the AdminLayout soon to be
            // LoaderLayout

            // Data/Loading Layout
            const lazyDataLayout = React.lazy(() => import("./components/data/DataLayout"));

            // Data File Loader Layout
            const lazyDataFileLoaderLayout = React.lazy(() => import("./packages/dataloader/DataLoaderLayout"));

            // Role Features
            if (roleFeatures) {
                // List Loaders FeatureType = 1
                const loadingFeatures = roleFeatures.filter(feature => feature.featureType === 1 || feature.featureType === 2);
                loadingFeatures.sort((a, b) => (a.options.feature.label > b.options.feature.label) ? 1 : -1);
                if (loadingFeatures.length > 0) {
                    // Load up the parent/summary for the loaders
                    routes.push({
                        seqNo: -2112                // NOTE: Always bottom above security / admin
                        ,label: 'Data'
                        ,pageTitle: 'Data Loading'
                        ,tooltip: ''
                        ,route: '/data'
                        ,navDrawer: true
                        ,navParent: ''
                        ,icon: formatIcon('SystemUpdateAltIcon')
                        ,component: lazyDataLayout});

                    // Add the loaders
                    for (let loadingFeature of loadingFeatures) {

                        // console.log(loadingFeature);

                        routes.push({
                            seqNo: loadingFeature.seqNo,
                            label: loadingFeature.options.feature.label,
                            pageTitle: loadingFeature.options.feature.label,
                            tooltip: '',
                            route: '/data/' + loadingFeature.options.feature.route,
                            navDrawer: true,
                            navParent: 'Data',
                            icon: loadingFeature?.options?.feature?.icon ? formatIcon(loadingFeature?.options?.feature?.icon) : '<SettingsIcon/>',
                            component: lazyDataFileLoaderLayout
                        });
                    }
                }
            }

            // **************************************************
            //      Package: Security
            // **************************************************
            const lazySecurityLayout = React.lazy(() => import("./components/security/SecurityLayout"));

            // Role Features
            if (roleFeatures) {
                // Security stuff 1000 = Users, 1001 = Features, 1002 = Roles, 1003 = Model Types, 1010 = Project Hub Setup
                const securityFeatures = roleFeatures.filter(feature => feature.featureType >= 1000);
				securityFeatures.sort((a, b) => (a.seqNo < b.seqNo) ? 1 : -1);
                if (securityFeatures.length > 0) {
                    // Security Layout
                    routes.push({
                        seqNo: -2113                 // NOTE: Always bottom below loaders
                        ,label: 'Security-Nav'
                        ,pageTitle: 'Security-PageTitle'
                        ,tooltip: ''
                        ,route: '/security'
                        ,navDrawer: true
                        ,navParent: ''
                        ,icon: formatIcon('SecurityIcon')
                        ,component: lazySecurityLayout });

                    const userFeature = securityFeatures.filter(feature => feature.featureType === 1000);

                    if (userFeature && userFeature.length > 0) {
                        // User Layout
                        const lazyUserLayout = React.lazy(() => import("./components/security/user/UserLayout"));

                        routes.push({
							seqNo: userFeature[0].seqNo,
							label: 'Users-Nav',
                            pageTitle: 'Users-PageTitle',
                            tooltip: '',
                            route: '/security/users',
                            navDrawer: true,
                            navParent: 'Security-Nav',
                            icon: formatIcon('GroupIcon'),
                            component: lazyUserLayout
                        });
                    }

                    const featuresFeature = securityFeatures.filter(feature => feature.featureType === 1001);

                    if (featuresFeature && featuresFeature.length > 0) {

                        // Role Layout
                        const lazyFeatureLayout = React.lazy(() => import("./components/security/feature/FeatureLayout"));

                        routes.push({
							seqNo: featuresFeature[0].seqNo,
							label: 'Features-Nav',
                            pageTitle: 'Features-PageTitle',
                            tooltip: '',
                            route: '/security/features',
                            navDrawer: true,
                            navParent: 'Security-Nav',
                            icon: formatIcon('StarIcon'),
                            component: lazyFeatureLayout
                        });
                    }

                    const rolesFeature = securityFeatures.filter(feature => feature.featureType === 1002);

                    if (rolesFeature && rolesFeature.length > 0) {

                        // Role Layout
                        const lazyRoleLayout = React.lazy(() => import("./components/security/role/RoleLayout"));

                        routes.push({
							seqNo: rolesFeature[0].seqNo,
							label: 'Roles-Nav',
                            pageTitle: 'Roles-PageTitle',
                            tooltip: '',
                            route: '/security/roles',
                            navDrawer: true,
                            navParent: 'Security-Nav',
                            icon: formatIcon('CasinoIcon'),
                            component: lazyRoleLayout
                        });
                    }


					const modeltypeFeature = securityFeatures.filter(feature => feature.featureType === 1003);

					if (modeltypeFeature && modeltypeFeature.length > 0) {

						const lazyModelTypeLayout = React.lazy(() => import("./components/security/modeltype/ModelTypeLayout"));

						routes.push({
							seqNo: modeltypeFeature[0].seqNo,
							label: 'Model Type-Nav',
							pageTitle: 'Model Type-PageTitle',
							tooltip: '',
							route: '/security/modeltypes',
							navDrawer: true,
							navParent: 'Security-Nav',
							icon: formatIcon('FilterFramesIcon'),
							component: lazyModelTypeLayout
						});
					}

					// **************************************************
					//      Package: Project Hub - Setup Project
					// **************************************************
					const projectHubSetupFeature = securityFeatures.filter(feature => feature.featureType === 1010);

					if (projectHubSetupFeature && projectHubSetupFeature.length > 0 && projectHubSetupFeature[0].enabled) {

						const lazyProjectHubSetupLayout = React.lazy(() => import("./packages/projecthub/ProjectHubSetupLayout"));

						routes.push({
							seqNo: projectHubSetupFeature[0].seqNo,
							label: 'Project Hub Setup-Nav',
							pageTitle: 'Project Hub Setup-PageTitle',
							tooltip: '',
							route: '/projecthub/setup',
							navDrawer: true,
							navParent: 'Admin-Nav',
							icon: formatIcon('AccountTreeIcon'),
							component: lazyProjectHubSetupLayout
						});

						adminRoute = true;
					}
                }
            }

			if (adminRoute) {
				// Administration Layout
				const lazyAdminLayout = React.lazy(() => import("./packages/admin/AdminLayout"));

				routes.push({
					seqNo: -2111                 // NOTE: Always bottom above Data and Security
					,label: 'Admin-Nav'
					,pageTitle: 'Admin-PageTitle'
					,tooltip: ''
					,route: '/admin'
					,navDrawer: true
					,navParent: ''
					,icon: formatIcon('CreateNewFolderIcon')
					,component: lazyAdminLayout });
			}

            // No features enabled
            if (routes.length === 0) {
                routes.push({
                    label: 'Not Found',
                    pageTitle: 'Not Found',
                    tooltip: '',
                    route: '*',
                    navDrawer: false,
                    navParent: '',
                    icon: null,
                    component: FourOhFourLayout
                });
            }

            // Setup jsx for router
            const allRoutes =
                routes.map((r, i) => {
                    // console.log('r', r);
                    return (
                        <Route exact path={r.route} component={WaitingComponent(r.component)} key={i}/>
                    );
                })

            // Persist routes for routers and other components
            dispatch(appSetRoutes(routes));

            setRouteJSX(allRoutes);
        }
    }, [user.authorized, user.roleFeatures]);

    function WaitingComponent(Component) {
        return props => (
            <Suspense fallback={<AppLayout/>}>
                <Component {...props} />
            </Suspense>
        );
    }

    return (
        <Switch>
            {/*Packages/Features*/}
            {routeJSX}

            {/*Note: This is a catch all / default route*/}
            <Route path="*" children={<FourOhFourLayout/>}/>
        </Switch>
    );
}
